/* generated using openapi-typescript-codegen -- do no edit */
/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { CollectionInvitationResponse } from '../models/CollectionInvitationResponse';
import type { CancelablePromise } from '../core/CancelablePromise';
import { OpenAPI } from '../core/OpenAPI';
import { request as __request } from '../core/request';
export class CollectionSharingService {
    /**
     * List collection invitations
     * List all invitations sent for a specific collection. This endpoint allows collection owners and administrators to view all invitation statuses (pending, accepted, rejected, expired) for their collection.
     *
     * **Prerequisites:**
     * - User must have the `enable_shared_collections` feature flag enabled
     * - User must be either:
     * - The collection owner
     * - A user with `admin` permission level on this collection
     *
     * **Response Details:**
     * - Returns all invitations regardless of status by default
     * - Use the `status` query parameter to filter by specific status
     * - Includes invitation tokens for revocation purposes
     * - Shows complete invitation history for the collection
     *
     * **Permission Levels in Results:**
     * - `read`: Invitee can view collection and contents
     * - `write`: Invitee can view and modify collection
     * - `admin`: Invitee can view, modify, and manage permissions
     *
     * **Status Types:**
     * - `pending`: Invitation sent but not yet responded to
     * - `accepted`: Invitation accepted and permission granted
     * - `rejected`: Invitation rejected or revoked by owner/admin
     * - `expired`: Invitation expired after 7 days without response
     *
     * **Use Cases:**
     * - Track invitation status for collaboration management
     * - Identify pending invitations that need follow-up
     * - Audit collection access history
     * - Revoke pending invitations if needed
     *
     * **Example Workflow:**
     * ```
     * 1. Collection owner invites users:
     * POST /api/collections/{collection_id}/invite
     *
     * 2. Owner checks invitation status:
     * GET /api/collections/{collection_id}/invitations
     *
     * 3. Filter for pending invitations:
     * GET /api/collections/{collection_id}/invitations?status=pending
     *
     * 4. Revoke invitation if needed:
     * DELETE /api/collections/{collection_id}/invitations/{token}/revoke
     * ```
     *
     * @returns CollectionInvitationResponse Collection invitations retrieved successfully
     * @throws ApiError
     */
    public static listCollectionInvitations({
        collectionId,
        status,
    }: {
        /**
         * Collection identifier
         */
        collectionId: string,
        /**
         * Filter invitations by status
         */
        status?: 'pending' | 'accepted' | 'rejected' | 'expired',
    }): CancelablePromise<Array<CollectionInvitationResponse>> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/collections/{collection_id}/invitations',
            path: {
                'collection_id': collectionId,
            },
            query: {
                'status': status,
            },
            errors: {
                400: `Bad Request - Invalid status filter`,
                401: `Unauthorized access`,
                403: `Forbidden - Insufficient permissions`,
                404: `Collection not found`,
            },
        });
    }
    /**
     * Invite user to collection
     * Send an invitation to share a collection with another user by email or username.
     *
     * **User Validation:** The system validates that the specified user exists before sending the invitation.
     * If no user is found with the provided email or username, a 404 USER_NOT_FOUND error is returned.
     *
     * @returns any Invitation sent successfully
     * @throws ApiError
     */
    public static inviteUserToCollection({
        collectionId,
        requestBody,
    }: {
        /**
         * Collection identifier
         */
        collectionId: string,
        requestBody: {
            /**
             * Email of user to invite (either email or username required)
             */
            email?: string;
            /**
             * Username of user to invite (either email or username required)
             */
            username?: string;
            /**
             * Permission level to grant
             */
            permission: 'read' | 'write' | 'admin';
        },
    }): CancelablePromise<{
        /**
         * Invitation token
         */
        id?: string;
        /**
         * Collection ID
         */
        collection_id?: string;
        /**
         * Collection name
         */
        collection_name?: string;
        /**
         * Inviter's display name or email
         */
        inviter_name?: string;
        /**
         * Invitee's email
         */
        invitee_email?: string;
        /**
         * Permission level
         */
        permission?: 'read' | 'write' | 'admin';
        /**
         * Invitation status
         */
        status?: 'pending' | 'accepted' | 'rejected' | 'expired';
        /**
         * Invitation expiration time
         */
        expires_at?: string;
        /**
         * Invitation creation time
         */
        created_at?: string;
    }> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/api/collections/{collection_id}/invite',
            path: {
                'collection_id': collectionId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                400: `Bad request - invalid data or duplicate invitation`,
                401: `Unauthorized access`,
                403: `Forbidden - insufficient permissions`,
                404: `Collection or user not found`,
            },
        });
    }
    /**
     * Accept collection invitation
     * Accept an invitation to access a shared collection
     * @returns any Invitation accepted successfully
     * @throws ApiError
     */
    public static acceptCollectionInvitation({
        requestBody,
    }: {
        requestBody: {
            /**
             * The invitation token received
             */
            invitation_token: string;
        },
    }): CancelablePromise<{
        message?: string;
    }> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/api/collections/invitations/accept',
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                400: `Invalid or expired invitation`,
                401: `Unauthorized access`,
                404: `Invitation not found`,
            },
        });
    }
    /**
     * Reject collection invitation
     * Reject an invitation to access a shared collection
     * @returns any Invitation rejected successfully
     * @throws ApiError
     */
    public static rejectCollectionInvitation({
        invitationToken,
        requestBody,
    }: {
        /**
         * The invitation token to reject
         */
        invitationToken: string,
        requestBody: Record<string, any>,
    }): CancelablePromise<{
        message?: string;
    }> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/api/collections/invitations/{invitation_token}/reject',
            path: {
                'invitation_token': invitationToken,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                400: `Invalid or already processed invitation`,
                401: `Unauthorized access`,
                404: `Invitation not found`,
            },
        });
    }
    /**
     * Revoke pending invitation
     * Revoke a pending invitation (inviter or collection admin only)
     * @returns void
     * @throws ApiError
     */
    public static revokeCollectionInvitation({
        collectionId,
        invitationToken,
    }: {
        /**
         * Collection identifier
         */
        collectionId: string,
        /**
         * The invitation token to revoke
         */
        invitationToken: string,
    }): CancelablePromise<void> {
        return __request(OpenAPI, {
            method: 'DELETE',
            url: '/api/collections/{collection_id}/invitations/{invitation_token}/revoke',
            path: {
                'collection_id': collectionId,
                'invitation_token': invitationToken,
            },
            errors: {
                400: `Cannot revoke invitation (not pending or already processed)`,
                401: `Unauthorized access`,
                403: `No permission to revoke this invitation`,
                404: `Invitation or collection not found`,
            },
        });
    }
    /**
     * List user's invitations
     * Get list of invitations sent to the current user
     * @returns any Invitations retrieved successfully
     * @throws ApiError
     */
    public static listUserInvitations({
        status,
    }: {
        /**
         * Filter invitations by status
         */
        status?: 'pending' | 'accepted' | 'rejected' | 'expired',
    }): CancelablePromise<Array<{
        id?: string;
        collection_id?: string;
        collection_name?: string;
        inviter_name?: string;
        permission?: 'read' | 'write' | 'admin';
        status?: 'pending' | 'accepted' | 'rejected' | 'expired';
        expires_at?: string;
        created_at?: string;
    }>> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/collections/invitations',
            query: {
                'status': status,
            },
            errors: {
                400: `Bad request - Invalid status filter value`,
                401: `Unauthorized access`,
            },
        });
    }
    /**
     * List shared collections
     * Get collections shared with the current user
     * @returns any Shared collections retrieved successfully
     * @throws ApiError
     */
    public static listSharedCollections(): CancelablePromise<Array<{
        /**
         * Collection ID
         */
        id?: string;
        /**
         * Collection name
         */
        name?: string;
        /**
         * Collection description
         */
        description?: string;
        /**
         * User's permission level
         */
        permission?: 'read' | 'write' | 'admin';
        owner?: {
            id?: string;
            username?: string;
            email?: string;
        };
        /**
         * When collection was shared with user
         */
        shared_at?: string;
    }>> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/collections/shared',
            errors: {
                401: `Unauthorized access`,
            },
        });
    }
    /**
     * Leave shared collection
     * Remove yourself from a shared collection (cannot be used by collection owner)
     * @returns void
     * @throws ApiError
     */
    public static leaveSharedCollection({
        collectionId,
    }: {
        /**
         * Collection identifier
         */
        collectionId: string,
    }): CancelablePromise<void> {
        return __request(OpenAPI, {
            method: 'DELETE',
            url: '/api/collections/{collection_id}/leave',
            path: {
                'collection_id': collectionId,
            },
            errors: {
                400: `Cannot leave collection (owner cannot leave own collection)`,
                401: `Unauthorized access`,
                403: `No access to this collection`,
                404: `Collection not found`,
            },
        });
    }
}
